import React, { useState, useEffect } from 'react';
import agentApiService from '../../services/agentApiService';
import './BookingHistory.css';

const BookingHistory = () => {
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [bookingHistory, setBookingHistory] = useState([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [filteredHistory, setFilteredHistory] = useState([]);

  useEffect(() => {
    fetchBookingHistory();
  }, []);

  useEffect(() => {
    // Filter history based on search term and status filter
    let filtered = bookingHistory;

    if (searchTerm.trim() !== '') {
      filtered = filtered.filter(item =>
        (item.bookingId && item.bookingId.toLowerCase().includes(searchTerm.toLowerCase())) ||
        (item.hotelName && item.hotelName.toLowerCase().includes(searchTerm.toLowerCase())) ||
        (item.hotelBookingId && item.hotelBookingId.toLowerCase().includes(searchTerm.toLowerCase())) ||
        (item.confirmationNumber && item.confirmationNumber.toLowerCase().includes(searchTerm.toLowerCase())) ||
        (item.address && item.address.toLowerCase().includes(searchTerm.toLowerCase())) ||
        (item.phoneNo && item.phoneNo.toLowerCase().includes(searchTerm.toLowerCase()))
      );
    }

    if (statusFilter !== 'all') {
      filtered = filtered.filter(item => 
        item.status && item.status.toLowerCase() === statusFilter.toLowerCase()
      );
    }

    setFilteredHistory(filtered);
  }, [bookingHistory, searchTerm, statusFilter]);

  const fetchBookingHistory = async () => {
    try {
      setLoading(true);
      setError(null);
      
      console.log('📋 [BOOKING-HISTORY] Fetching booking history...');
      const response = await agentApiService.getBookingHistory();
      
      if (response.success) {
        setBookingHistory(response.details || []);
        console.log('✅ [BOOKING-HISTORY] Booking history loaded successfully:', response.details?.length, 'records');
      } else {
        throw new Error(response.message || 'Failed to fetch booking history');
      }
    } catch (err) {
      console.error('❌ [BOOKING-HISTORY] Error fetching booking history:', err);
      setError(err.message || 'Failed to load booking history');
    } finally {
      setLoading(false);
    }
  };

  const getStatusBadgeClass = (status) => {
    if (!status) return 'badge bg-secondary text-white rounded-pill';
    
    switch (status.toUpperCase()) {
      case 'BOOKED':
        return 'badge bg-success text-white rounded-pill';
      case 'CANCELLED':
        return 'badge bg-danger text-white rounded-pill';
      case 'HOLD':
      case 'IN_PROGRESS':
      case 'PENDING':
      case 'NOT_SET':
      case 'REJECTED':
        return 'badge bg-warning text-dark rounded-pill';
      default:
        return 'badge bg-secondary text-white rounded-pill';
    }
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    try {
      return new Date(dateString).toLocaleDateString('en-US', {
        year: 'numeric',
        month: 'short',
        day: 'numeric'
      });
    } catch (e) {
      return dateString;
    }
  };

  const getUniqueStatuses = () => {
    const statuses = [...new Set(bookingHistory.map(item => item.status).filter(Boolean))];
    return statuses.sort();
  };

  const getStatusStats = () => {
    const stats = {
      total: bookingHistory.length,
      booked: bookingHistory.filter(item => item.status === 'BOOKED').length,
      cancelled: bookingHistory.filter(item => item.status === 'CANCELLED').length,
      pending: bookingHistory.filter(item => ['HOLD', 'IN_PROGRESS', 'PENDING', 'NOT_SET', 'REJECTED'].includes(item.status)).length
    };
    return stats;
  };

  if (loading) {
    return (
      <div className="booking-history-container">
        <div className="loading-spinner">
          <div className="spinner"></div>
          <p>Loading booking history...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="booking-history-container">
        <div className="error-message">
          <h3>Error Loading Booking History</h3>
          <p>{error}</p>
          <button onClick={fetchBookingHistory} className="retry-button">
            Try Again
          </button>
        </div>
      </div>
    );
  }

  const stats = getStatusStats();

  return (
    <div className="booking-history-container">
      {/* Header */}
      <div className="page-header">
        <h1>Booking History</h1>
        <p>View your complete booking history and transaction records</p>
      </div>

      {/* Statistics Cards */}
      <div className="stats-grid">
        <div className="stat-card">
          <div className="stat-icon">📊</div>
          <div className="stat-content">
            <h3>Total Bookings</h3>
            <p className="stat-value">{stats.total}</p>
          </div>
        </div>
        <div className="stat-card">
          <div className="stat-icon">✅</div>
          <div className="stat-content">
            <h3>Booked</h3>
            <p className="stat-value">{stats.booked}</p>
          </div>
        </div>
        <div className="stat-card">
          <div className="stat-icon">❌</div>
          <div className="stat-content">
            <h3>Cancelled</h3>
            <p className="stat-value">{stats.cancelled}</p>
          </div>
        </div>
        <div className="stat-card">
          <div className="stat-icon">⏳</div>
          <div className="stat-content">
            <h3>Pending</h3>
            <p className="stat-value">{stats.pending}</p>
          </div>
        </div>
      </div>

      {/* Controls */}
      <div className="controls-section">
        <div className="search-controls">
          <div className="search-box">
            <input
              type="text"
              placeholder="Search by Booking ID, Hotel Name, Confirmation Number..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="search-input"
            />
            <span className="search-icon">🔍</span>
          </div>
        </div>
        
        <div className="filter-controls">
          <label>
            Status:
            <select 
              value={statusFilter} 
              onChange={(e) => setStatusFilter(e.target.value)}
              className="status-filter"
            >
              <option value="all">All Status</option>
              {getUniqueStatuses().map(status => (
                <option key={status} value={status}>{status}</option>
              ))}
            </select>
          </label>
        </div>
      </div>

      {/* Booking History Table */}
      <div className="history-table-container">
        <div className="table-card">
          <div className="table-header">
            <h3>Booking History Details</h3>
            <div className="table-info">
              Showing {filteredHistory.length} of {bookingHistory.length} entries
            </div>
          </div>
          
          <div className="table-wrapper">
            <table className="history-table">
              <thead>
                <tr>
                  <th>Booking ID</th>
                  <th>Hotel Name</th>
                  <th>Hotel Booking ID</th>
                  <th>Check-In Date</th>
                  <th>Check-Out Date</th>
                  <th>City</th>
                  <th>Phone No</th>
                  <th>Confirmation Number</th>
                  <th>Booking Date</th>
                  <th>Status</th>
                </tr>
              </thead>
              <tbody>
                {filteredHistory.length > 0 ? (
                  filteredHistory.map((item, index) => (
                    <tr key={item.bookingId || index} className="table-row">
                      <td className="booking-id">
                        <a 
                          href={`/agent/dashboard/booking-details/${item.bookingId}`}
                          className="booking-link"
                        >
                          {item.bookingId || 'N/A'}
                        </a>
                      </td>
                      <td className="hotel-name">{item.hotelName || 'N/A'}</td>
                      <td className="hotel-booking-id">{item.hotelBookingId || '-'}</td>
                      <td className="check-in-date">{formatDate(item.checkInDate)}</td>
                      <td className="check-out-date">{formatDate(item.checkOutDate)}</td>
                      <td className="city">{item.address || 'N/A'}</td>
                      <td className="phone-no">{item.phoneNo || 'N/A'}</td>
                      <td className="confirmation-number">{item.confirmationNumber || 'N/A'}</td>
                      <td className="booking-date">{formatDate(item.bookingDate)}</td>
                      <td className="status">
                        <span className={getStatusBadgeClass(item.status)}>
                          {item.status || '-'}
                        </span>
                      </td>
                    </tr>
                  ))
                ) : (
                  <tr>
                    <td colSpan="10" className="no-data">
                      {searchTerm || statusFilter !== 'all' 
                        ? 'No matching booking records found' 
                        : 'No booking history available'}
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        </div>
      </div>
    </div>
  );
};

export default BookingHistory;